import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import PaymentReportFilters from '../components/PaymentReportFilters';
import PaymentSummaryAnalytics from '../components/PaymentSummaryAnalytics';
import Papa from 'papaparse';

const { FiDollarSign, FiDownload, FiFileText, FiCheckCircle, FiAlertCircle, FiClock, FiSearch, FiArrowRight, FiActivity } = FiIcons;

const PaymentHistoryReports = () => {
  const { data } = useStore();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    propertyId: 'all',
    tenantId: 'all',
    status: 'all'
  });

  const enrichedPayments = useMemo(() => {
    return (data.rentPayments || []).map(p => {
      const lease = data.leases.find(l => l.id === p.leaseId);
      const property = data.properties.find(prop => prop.id === lease?.propertyId);
      const tenant = data.tenants.find(t => t.id === lease?.tenantId);
      return { ...p, lease, property, tenant };
    });
  }, [data]);

  const filteredPayments = useMemo(() => {
    return enrichedPayments.filter(p => {
      const pDate = parseISO(p.dueDate);
      
      if (filters.startDate || filters.endDate) {
        const start = filters.startDate ? startOfDay(parseISO(filters.startDate)) : new Date(0);
        const end = filters.endDate ? endOfDay(parseISO(filters.endDate)) : new Date(8640000000000000);
        if (!isWithinInterval(pDate, { start, end })) return false;
      }

      if (filters.propertyId !== 'all' && p.property?.id !== filters.propertyId) return false;
      if (filters.tenantId !== 'all' && p.tenant?.id !== filters.tenantId) return false;
      if (filters.status !== 'all' && p.status !== filters.status) return false;

      return true;
    });
  }, [enrichedPayments, filters]);

  const stats = {
    total: filteredPayments.reduce((sum, p) => sum + p.amount, 0),
    collected: filteredPayments.filter(p => p.status === 'Paid').reduce((sum, p) => sum + p.amount, 0),
    outstanding: filteredPayments.filter(p => p.status !== 'Paid').reduce((sum, p) => sum + p.amount, 0),
    count: filteredPayments.length
  };

  const handleExport = () => {
    const exportData = filteredPayments.map(p => ({
      Month: p.month,
      Property: p.property?.name,
      Tenant: p.tenant?.name,
      'Due Date': p.dueDate,
      Amount: p.amount,
      Status: p.status,
      'Payment Date': p.paymentDate || 'N/A'
    }));
    
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payment_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Financial Audit Reports</h2>
          <p className="text-sm text-gray-500 font-medium italic">Deep-dive historical analysis and ledger reconciliation.</p>
        </div>
        <button 
          onClick={handleExport}
          className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
        >
          <SafeIcon icon={FiDownload} className="mr-2" /> Export CSV Ledger
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative group">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Receivables</p>
              <p className="text-3xl font-black text-gray-900">${stats.total.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">{stats.count} Total Cycles</p>
              <SafeIcon icon={FiActivity} className="absolute -bottom-2 -right-2 text-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
            </div>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative group">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Actual Collected</p>
              <p className="text-3xl font-black text-green-600">${stats.collected.toLocaleString()}</p>
              <div className="mt-2 w-full bg-gray-50 h-1 rounded-full overflow-hidden">
                <div className="bg-green-500 h-full transition-all duration-1000" style={{ width: `${(stats.collected / (stats.total || 1)) * 100}%` }} />
              </div>
            </div>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative group">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Outstanding</p>
              <p className="text-3xl font-black text-red-600">${stats.outstanding.toLocaleString()}</p>
              <p className="text-[10px] text-red-500 font-bold uppercase mt-1">
                {Math.round((stats.outstanding / (stats.total || 1)) * 100)}% Leakage Rate
              </p>
            </div>
          </div>
          <PaymentReportFilters 
            onFilterChange={setFilters} 
            properties={data.properties} 
            tenants={data.tenants} 
          />
        </div>
        <div className="lg:col-span-1">
          <PaymentSummaryAnalytics payments={filteredPayments} />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
          <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiFileText} className="text-indigo-600" /> Transaction Audit Trail
          </h3>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Showing {filteredPayments.length} Records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/20">
              <tr>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Cycle & Date</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Property & Tenant</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Payment Data</th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredPayments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-6">
                    <p className="text-sm font-black text-gray-900">{p.month}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">Due: {p.dueDate}</p>
                  </td>
                  <td className="px-6 py-6">
                    <p className="text-sm font-black text-gray-900">{p.property?.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <div className="w-4 h-4 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center text-[8px] font-black">
                        {p.tenant?.name[0]}
                      </div>
                      <p className="text-[10px] text-gray-500 font-medium">{p.tenant?.name}</p>
                    </div>
                  </td>
                  <td className="px-6 py-6">
                    <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 w-fit ${
                      p.status === 'Paid' ? 'bg-green-50 text-green-600' : 
                      p.status === 'Overdue' ? 'bg-red-50 text-red-600' : 
                      'bg-indigo-50 text-indigo-600'
                    }`}>
                      <SafeIcon icon={p.status === 'Paid' ? FiCheckCircle : p.status === 'Overdue' ? FiAlertCircle : FiClock} size={12} />
                      {p.status}
                    </span>
                  </td>
                  <td className="px-6 py-6 font-medium text-xs text-gray-500">
                    {p.status === 'Paid' ? (
                      <div className="flex flex-col">
                        <span className="text-gray-900 font-black">Settled</span>
                        <span>On {p.paymentDate || 'N/A'}</span>
                      </div>
                    ) : (
                      <span className="italic">Awaiting Transfer</span>
                    )}
                  </td>
                  <td className="px-6 py-6 text-right">
                    <p className="text-sm font-black text-gray-900">${p.amount.toLocaleString()}</p>
                    {p.status === 'Overdue' && (
                      <p className="text-[9px] text-red-600 font-bold uppercase mt-1">+ Late Fee Pending</p>
                    )}
                  </td>
                </tr>
              ))}
              {filteredPayments.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-24 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
                      <SafeIcon icon={FiSearch} size={32} className="text-gray-200" />
                    </div>
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No matching payment records found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryReports;